<template>
  <OnboardingLayout
    :is-next-allowed="true"
    :is-end="isEnd"
    :is-next-loading="isNextLoading"
    :is-prev-button-shown="true"
    @next-step="$emit('nextStep')"
    @prev-step="$emit('prevStep')"
  >
    <template #left>
      <h1>How did you hear about us?</h1>
      <h2>Help us understand how you discovered Routine.</h2>

      <div class="blocks">
        <div
          v-for="option in options"
          :key="option.key"
          class="block md"
          :class="{ isSelected: answer === option.key }"
          @click="$emit('update:answer', option.key)"
        >
          <div class="icon">
            <span class="letter">{{ option.letter }}</span>
          </div>
          <div class="content">
            <h3>{{ option.label }}</h3>
          </div>
        </div>
      </div>
    </template>

    <template #right>
      <img :src="getImageUrl(imgName)" />
    </template>
  </OnboardingLayout>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from "vue";

import OnboardingLayout from "@/components/onboarding/OnboardingLayout.vue";
import { metrics } from "@/metrics";

export type ChannelType =
  | "friend_colleague"
  | "social_media"
  | "ai_chat"
  | "app_store"
  | "search_engine"
  | "other"
  | undefined;

export default defineComponent({
  components: {
    OnboardingLayout,
  },
  props: {
    answer: {
      type: String as PropType<ChannelType>,
      required: false,
      default: undefined,
    },
    isNextLoading: { type: Boolean, required: false, default: false },
    /**
     * Whether the next click ends onboarding and launches the app (true) or
     * another step follows (false) — e.g. the plan step on the team path.
     * Decides the button label: Launch vs Continue.
     */
    isEnd: { type: Boolean, required: false, default: true },
  },
  emits: {
    nextStep: () => true,
    prevStep: () => true,
    "update:answer": (_n: ChannelType) => true,
  },
  setup() {
    const imgName = ref<string>("step_org");
    const options = [
      { key: "friend_colleague", letter: "A", label: "Friend / colleague" },
      {
        key: "social_media",
        letter: "B",
        label: "Social media, YouTube, or podcasts",
      },
      { key: "ai_chat", letter: "C", label: "AI chat (ChatGPT, Gemini, etc.)" },
      {
        key: "app_store",
        letter: "D",
        label: "App Store (Google Play, Apple)",
      },
      {
        key: "search_engine",
        letter: "E",
        label: "Search engine (Google, Bing, etc.)",
      },
      { key: "other", letter: "F", label: "Other" },
    ] as const;

    metrics.segment.page("Onboarding", { step: "channel" });

    const getImageUrl = (name: string) => {
      return new URL(`../../assets/onboarding/${name}.png`, import.meta.url)
        .href;
    };

    return {
      imgName,
      options,
      getImageUrl,
    };
  },
});
</script>

<style scoped lang="scss">
.icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.letter {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  @extend %font-caption-medium;
  @include themed() {
    border: 1px solid t($grey-20);
    color: t($grey-70);
    background-color: t($grey-05);
  }
}
</style>
